import { Trash2, Plus } from 'lucide-react'
import { Button, Input, Select } from '@/components/ui'
import type { DamagedItem } from '../api/types'

interface Props {
  items: DamagedItem[]
  onChange: (items: DamagedItem[]) => void
  readOnly?: boolean
  variant: 'equipment' | 'material'
}

function blankItem(variant: 'equipment' | 'material'): DamagedItem {
  return {
    category: variant,
    description: '',
    asset_id: '',
    product_code: '',
    lot_serial: '',
    quantity_affected: '',
    unit_of_measure: '',
    estimated_damage_value: '',
    repairable: '',
  }
}

export function DamagedItemsEditor({ items, onChange, readOnly = false, variant }: Props) {
  const rows = items.length > 0 ? items : [blankItem(variant)]

  function update(idx: number, field: keyof DamagedItem, value: string) {
    const next = rows.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item
    )
    onChange(next)
  }

  function addRow() {
    onChange([...rows, blankItem(variant)])
  }

  function removeRow(idx: number) {
    if (rows.length === 1) {
      onChange([blankItem(variant)])
    } else {
      onChange(rows.filter((_, i) => i !== idx))
    }
  }

  if (variant === 'equipment') {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-[2fr_1fr_1fr_0.6fr_0.6fr_1fr_1fr_36px] gap-2 px-1">
          {['Description', 'Asset / Tag #', 'Product Code', 'Qty', 'UOM', 'Est. Value ($)', 'Repairable?', ''].map((h) => (
            <span key={h} className="text-xs font-medium text-secondary uppercase tracking-wide">{h}</span>
          ))}
        </div>
        {rows.map((item, idx) => (
          <div key={idx} className="grid grid-cols-[2fr_1fr_1fr_0.6fr_0.6fr_1fr_1fr_36px] gap-2 items-start">
            <Input value={item.description ?? ''} onChange={(e) => update(idx, 'description', e.target.value)} placeholder="Description of damage" disabled={readOnly} />
            <Input value={item.asset_id ?? ''} onChange={(e) => update(idx, 'asset_id', e.target.value)} placeholder="Tag #" disabled={readOnly} />
            <Input value={item.product_code ?? ''} onChange={(e) => update(idx, 'product_code', e.target.value)} placeholder="SKU" disabled={readOnly} />
            <Input value={String(item.quantity_affected ?? '')} onChange={(e) => update(idx, 'quantity_affected', e.target.value)} placeholder="0" type="number" min={0} disabled={readOnly} />
            <Input value={item.unit_of_measure ?? ''} onChange={(e) => update(idx, 'unit_of_measure', e.target.value)} placeholder="ea" disabled={readOnly} />
            <Input value={String(item.estimated_damage_value ?? '')} onChange={(e) => update(idx, 'estimated_damage_value', e.target.value)} placeholder="0.00" type="number" min={0} step={0.01} disabled={readOnly} />
            <Select value={item.repairable ?? ''} onChange={(e) => update(idx, 'repairable', e.target.value)} disabled={readOnly}>
              <option value="">—</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
              <option value="tbd">TBD</option>
            </Select>
            {!readOnly ? (
              <button type="button" onClick={() => removeRow(idx)} className="h-8 w-8 flex items-center justify-center rounded text-secondary hover:text-danger hover:bg-danger/10 transition-colors" title="Remove item">
                <Trash2 size={14} />
              </button>
            ) : <div />}
          </div>
        ))}
        {!readOnly && (
          <Button variant="ghost" size="sm" onClick={addRow} className="mt-1">
            <Plus size={14} className="mr-1" /> Add item
          </Button>
        )}
      </div>
    )
  }

  // Material variant — no Asset ID, no Repairable
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[2fr_1fr_1fr_0.6fr_0.6fr_1fr_36px] gap-2 px-1">
        {['Description', 'Product Code', 'Lot / Serial #', 'Qty', 'UOM', 'Est. Value ($)', ''].map((h) => (
          <span key={h} className="text-xs font-medium text-secondary uppercase tracking-wide">{h}</span>
        ))}
      </div>
      {rows.map((item, idx) => (
        <div key={idx} className="grid grid-cols-[2fr_1fr_1fr_0.6fr_0.6fr_1fr_36px] gap-2 items-start">
          <Input value={item.description ?? ''} onChange={(e) => update(idx, 'description', e.target.value)} placeholder="Description of damage" disabled={readOnly} />
          <Input value={item.product_code ?? ''} onChange={(e) => update(idx, 'product_code', e.target.value)} placeholder="SKU" disabled={readOnly} />
          <Input value={item.lot_serial ?? ''} onChange={(e) => update(idx, 'lot_serial', e.target.value)} placeholder="Lot / Serial" disabled={readOnly} />
          <Input value={String(item.quantity_affected ?? '')} onChange={(e) => update(idx, 'quantity_affected', e.target.value)} placeholder="0" type="number" min={0} disabled={readOnly} />
          <Input value={item.unit_of_measure ?? ''} onChange={(e) => update(idx, 'unit_of_measure', e.target.value)} placeholder="kg" disabled={readOnly} />
          <Input value={String(item.estimated_damage_value ?? '')} onChange={(e) => update(idx, 'estimated_damage_value', e.target.value)} placeholder="0.00" type="number" min={0} step={0.01} disabled={readOnly} />
          {!readOnly ? (
            <button type="button" onClick={() => removeRow(idx)} className="h-8 w-8 flex items-center justify-center rounded text-secondary hover:text-danger hover:bg-danger/10 transition-colors" title="Remove item">
              <Trash2 size={14} />
            </button>
          ) : <div />}
        </div>
      ))}
      {!readOnly && (
        <Button variant="ghost" size="sm" onClick={addRow} className="mt-1">
          <Plus size={14} className="mr-1" /> Add item
        </Button>
      )}
    </div>
  )
}
